import Phaser from "phaser";

import { WORKSHOP_ENERGY_ART } from "../config/artAssets";

export type EnergyVisualKind = "capacity" | "recovery" | "sigil";

export class EnergyMechanismScene extends Phaser.Scene {
  readonly #kind: EnergyVisualKind;
  readonly #renderScale: number;
  readonly #onReady: () => void;
  #reducedMotion: boolean;
  #chargeRatio: number;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #object!: Phaser.GameObjects.Container;
  #image!: Phaser.GameObjects.Image;
  #luminosity!: Phaser.GameObjects.Image;
  #ambientTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    kind: EnergyVisualKind,
    reducedMotion: boolean,
    chargeRatio: number,
    renderScale: number,
    onReady: () => void,
  ) {
    super({ key: `EnergyMechanism-${kind}` });
    this.#kind = kind;
    this.#reducedMotion = reducedMotion;
    this.#chargeRatio = Phaser.Math.Clamp(chargeRatio, 0, 1);
    this.#renderScale = renderScale;
    this.#onReady = onReady;
  }

  preload(): void {
    const art = WORKSHOP_ENERGY_ART[this.#kind];
    this.load.image(art.key, art.url);
  }

  create(): void {
    const art = WORKSHOP_ENERGY_ART[this.#kind];
    this.#image = this.add.image(0, 0, art.key).setDepth(2);
    this.#luminosity = this.add
      .image(0, 0, art.key)
      .setTint(0xb8fff8)
      .setAlpha(0.1)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(3);
    this.#object = this.add.container(0, 0, [this.#image, this.#luminosity]).setDepth(2);
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyCharge();
    this.#restartMotion();
    this.#onReady();
  }

  setCharge(current: number, capacity: number): void {
    const nextRatio = Phaser.Math.Clamp(current / Math.max(1, capacity), 0, 1);
    if (nextRatio === this.#chargeRatio) return;
    this.#chargeRatio = nextRatio;
    if (this.sys.isActive()) this.#applyCharge();
  }

  setReducedMotion(reducedMotion: boolean): void {
    if (this.#reducedMotion === reducedMotion) return;
    this.#reducedMotion = reducedMotion;
    if (this.sys.isActive()) this.#restartMotion();
  }

  #layout(size: Phaser.Structs.Size): void {
    const width = Math.round(size.width / this.#renderScale);
    const height = Math.round(size.height / this.#renderScale);
    if (width <= 0 || height <= 0 || (width === this.#layoutWidth && height === this.#layoutHeight)) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main
      .setViewport(0, 0, Math.round(size.width), Math.round(size.height))
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);

    const art = WORKSHOP_ENERGY_ART[this.#kind];
    const widthRatio = this.#kind === "sigil" ? 0.86 : this.#kind === "capacity" ? 0.82 : 0.94;
    const heightRatio = this.#kind === "sigil" ? 0.92 : 0.95;
    const scale = Math.min((width * widthRatio) / art.width, (height * heightRatio) / art.height);
    const centerX = width / 2;
    const centerY = height * (this.#kind === "sigil" ? 0.51 : 0.5);
    this.#object.setPosition(centerX, centerY).setScale(scale);
    this.#restartMotion();
  }

  #applyCharge(): void {
    const ratio = this.#chargeRatio;
    this.#image.setAlpha(0.7 + ratio * 0.3);
    this.#luminosity.setAlpha(0.035 + ratio * 0.11);
    if (ratio <= 0.01) {
      this.#image.setTint(0x718d90);
    } else if (ratio <= 0.12) {
      this.#image.setTint(0xf0bd79);
    } else {
      this.#image.clearTint();
    }
  }

  #restartMotion(): void {
    if (this.#layoutHeight === 0) return;
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#object);
    this.tweens.killTweensOf(this.#luminosity);
    const centerY = this.#layoutHeight * (this.#kind === "sigil" ? 0.51 : 0.5);
    this.#object.setY(centerY);
    this.#luminosity.setAlpha(0.035 + this.#chargeRatio * 0.11);
    if (this.#reducedMotion) return;

    const distance = this.#kind === "sigil" ? 1.4 : 5;
    const duration = this.#kind === "sigil" ? 2_800 : 2_400;
    this.#ambientTweens.push(
      this.tweens.add({
        targets: this.#object,
        y: centerY - distance,
        duration,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
      this.tweens.add({
        targets: this.#luminosity,
        alpha: 0.09 + this.#chargeRatio * 0.12,
        duration: duration * 0.72,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
    );
  }
}
